/*
 * The API client.
 *
 * Every path is a bare `/api/...` because in production FastAPI serves both the
 * app and the API from one origin; in development Vite proxies it. There is
 * deliberately no configurable base URL - one less thing to get wrong between
 * environments.
 *
 * A 401 is not an error here, it is a state. The app has to be able to ask "am
 * I signed in" without treating the answer "no" as a failure, or every page
 * load while signed out throws.
 */

export class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    credentials: 'same-origin',
  })

  if (!res.ok) {
    let detail = res.statusText
    try {
      const body = await res.json()
      detail = body?.detail ?? detail
    } catch {
      /* a non-JSON error body is still an error; keep the status text */
    }
    throw new ApiError(res.status, detail)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export const api = {
  get: <T>(p: string) => request<T>(p),
  post: <T>(p: string, body?: unknown) =>
    request<T>(p, { method: 'POST', body: JSON.stringify(body ?? {}) }),
  put: <T>(p: string, body?: unknown) =>
    request<T>(p, { method: 'PUT', body: JSON.stringify(body ?? {}) }),
  patch: <T>(p: string, body?: unknown) =>
    request<T>(p, { method: 'PATCH', body: JSON.stringify(body ?? {}) }),
  del: <T>(p: string) => request<T>(p, { method: 'DELETE' }),
}

/* ------------------------------------------------------------------ types */
export interface User {
  id: number
  email: string
  name: string
  picture: string
  role: string
  is_owner: boolean
  can_write: boolean
  hidden_sections: string[]
}

// A section is visible unless the owner hid it for this person; the owner sees
// everything. Keep the keys in step with the backend SECTIONS catalog.
export function canSee(user: User, section: string): boolean {
  if (user.is_owner) return true
  return !(user.hidden_sections || []).includes(section)
}

export interface ManagedUser {
  id: number
  email: string
  name: string
  role: string
  status: string
  has_password: boolean
  google_linked: boolean
  hidden_sections: string[]
  last_login_at: string | null
}

export interface TrackRef { id: number; name: string; slug: string }
export interface LayoutRef { id: number; name: string; direction: string }

export interface SessionSummary {
  id: number
  run_id: number
  racer: string
  started_at: string | null

  /** IANA zone of the TRACK, so times render where they happened. */

  timezone: string
  /** Cadet 9 or Cadet 12. Null means nobody has said, and it is shown as
   *  unknown rather than folded in with either. */
  race_class: string | null
  n_laps: number
  best_lap_s: number | null
  duration_s: number
  track: TrackRef | null
  layout: LayoutRef | null
  event_id: number | null
  telemetry_complete: boolean
  setup_status: 'none' | 'partial' | 'saved' | 'hidden'
}

export interface Lap {
  number: number
  label: string
  duration_s: number
  is_valid: boolean
  video_offset_s: number | null
}

export interface SessionDetail extends SessionSummary {
  laps: Lap[]
  median_lap_s: number | null
  setup: { id: number; values: Record<string, string>; recorded_at: string | null } | null
  videos: { id: number; complete: boolean; duration_s: number | null;
            session_offset_s: number | null; matched_by: string }[]
  photos: { id: number; kind: string; caption: string }[]
}

export interface FieldDef {
  key: string
  label: string
  group: string
  kind: 'num' | 'text' | 'choice' | 'textarea'
  unit: string
  options: string[]
  hint: string
  more_means: string
  source: string
}

export interface SpecDef {
  key: string; label: string; kind: string; unit: string
  options: string[]; hint: string
}

export interface CategoryDef {
  key: string; label: string; plural: string
  track_term: string; positioned: boolean; specs: SpecDef[]
  count?: number
}

export interface Registry {
  groups: string[]
  fields: FieldDef[]
  categories: CategoryDef[]
}

export interface GaragePart {
  id: number; category: string; name: string
  position: string; specs: Record<string, string>; retired: boolean
}

export interface EventSlot {
  id: number; sequence: number; label: string; session_type: string
  planned_start: string | null; session_id: number | null
  grid_position: number | null; finish_position: number | null
  result_notes: string
}

export interface KartEvent {
  id: number; name: string; event_type: string; event_type_label: string
  is_structured: boolean; track: TrackRef | null
  start_date: string | null; end_date: string | null; notes: string
  slots: EventSlot[]
}

/** Best lap keyed by class, plus "unknown" for sessions nobody has tagged.
 *  Never collapsed into one number: Cadet 12 runs no restrictor and is about a
 *  second a lap quicker, so a combined best is only ever the Cadet 12 time
 *  wearing both hats. */
export type BestByClass = Record<string, number>

export interface TrackRow {
  id: number; slug: string; name: string; location: string; state: string
  country: string; layouts: LayoutRef[]
  address: string | null
  latitude: number | null; longitude: number | null
  weather_station: string | null; weather_wmo: string | null
  sessions: number; best_lap_s: number | null; last_visited: string | null
  best_by_class: BestByClass
}

/** A layout on the track detail page carries its own history, because layouts
 *  are never merged - a reversed circuit is not the same corners. */
export interface LayoutStats extends LayoutRef {
  lap_length_m: number | null
  sessions: number
  best_lap_s: number | null
  best_by_class: BestByClass
  last_visited: string | null
}

export interface TrackDetail extends Omit<TrackRow, 'layouts'> {
  weather_product: string | null
  layouts: LayoutStats[]
}

export interface StationHit {
  name: string; product: string; wmo: string; distance_km: number
}
export interface ResolveStationResult {
  latitude: number | null; longitude: number | null
  source: string | null
  station: StationHit | null
}

export interface ChangeAdvice {
  changed: string[]
  count: number
  level: 'ok' | 'note' | 'warn'
  message: string
}



export interface EngineProfile {
  top_speed_kmh: number; peak_rpm: number | null; straight_pull_kmh_s: number
}
export type CornerPattern = 'mid_lost' | 'entry_slower' | 'exit_slower' | 'quicker'
export interface CornerSpeeds { entry: number; mid: number; exit: number; time_s: number }
export interface CornerRow {
  corner: number; at_pct: number
  a: CornerSpeeds; b: CornerSpeeds; delta: CornerSpeeds
  called: { entry: boolean; mid: boolean; exit: boolean; time: boolean }
  pattern: CornerPattern | null; pattern_text: string | null
}
export interface TrackMapData {
  points: [number, number][]
  corners: { corner: number; from: number; to: number; label: [number, number] }[]
}
export interface LapOffered {
  lap_number: number; label: string; duration_s: number; clean: boolean; used: boolean
}
export interface CornerCompare {
  laps_a: number; laps_b: number; corner_time_delta_s: number
  corners: CornerRow[]; map: TrackMapData; numbered_from_run: number | null
}
export interface CompareSide { id: number; run_id: number; layout_id: number | null;
  layout_name: string | null; layout_direction: string | null;
  best_lap_s: number | null; race_class: string | null }
export interface CompareResult {
  a: CompareSide; b: CompareSide
  same_layout: boolean
  lap_delta_s: number | null
  engine: {
    a: EngineProfile; b: EngineProfile
    deltas: { top_speed_kmh: number | null; peak_rpm: number | null; straight_pull_kmh_s: number | null }
    b_stronger: boolean
    verdict: 'stronger' | 'weaker' | 'similar'
    note: string | null
  }
  chassis: {
    b_better: boolean
    verdict: string
    note: string | null
  }
  corners: CornerCompare | null
  laps: { a: LapOffered[]; b: LapOffered[]; picked_a: boolean; picked_b: boolean }
  traces: { a: { d: number; kmh: number }[]; b: { d: number; kmh: number }[] } | null
}


export interface TuneConditions {
  track: string | null; station: string | null; observed_at: string | null
  air_temp_c: number | null; humidity_pct: number | null; pressure_hpa: number | null
  dewpoint_c: number | null; rad: number | null; density_alt_ft: number | null
  label: string | null
}
export interface JetNeighbour {
  session_id?: number; label?: string; main_jet: number; ratio: number
  best_lap_s?: number | null; distance?: number
}
export interface TuneCarb {
  float_height_mm: number | null; float_method: string; return_line: string
}
export interface TuneLocation { slug: string; name: string; mapped: boolean }
export interface TuneResult {
  conditions: TuneConditions | null
  recommendation: { recommended: number | null; confidence: string; reason: string; neighbours: JetNeighbour[] } | null
  current_jet: { main_jet: number; needle_clip: string; from: string; ratio: number } | null
  carb: TuneCarb
  locations: TuneLocation[]
  selected_location: string | null
  class: string
  history_count: number
  history: { session_id: number; label: string; main_jet: number; needle_clip: string; ratio: number; best_lap_s: number | null; air_temp_c: number | null; race_class: string }[]
}

export interface AdviceChange {
  field_key: string; label: string; direction: string; why: string; confidence: string
}
export interface SetupAdvice {
  headline: string
  read: string
  first_change: AdviceChange | null
  then: AdviceChange[]
  watch: string[]
  cautions: string[]
}
export interface AdviceResult {
  advice: SetupAdvice
  setup_recorded: { a: boolean; b: boolean }
}
